import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Globe } from "lucide-react";

export const LANGUAGES = [
  ["es", "Español", "🇲🇽"], ["en", "English", "🇺🇸"], ["pt", "Português", "🇧🇷"], ["fr", "Français", "🇫🇷"], ["de", "Deutsch", "🇩🇪"], ["it", "Italiano", "🇮🇹"],
  ["zh-CN", "中文", "🇨🇳"], ["ja", "日本語", "🇯🇵"], ["ko", "한국어", "🇰🇷"], ["ar", "العربية", "🇸🇦"], ["ru", "Русский", "🇷🇺"], ["hi", "हिन्दी", "🇮🇳"],
];
const SOURCE = "es";
const STORAGE = "hpf_lang";

const readCookieLang = () => {
  const m = document.cookie.match(/(?:^|; )googtrans=\/es\/([\w-]+)/);
  return m ? m[1] : SOURCE;
};

const setCookie = (lang) => {
  const host = window.location.hostname;
  const value = lang === SOURCE ? "" : `/es/${lang}`;
  const expires = lang === SOURCE ? "Thu, 01 Jan 1970 00:00:00 GMT" : "Fri, 31 Dec 2037 23:59:59 GMT";
  document.cookie = `googtrans=${value}; expires=${expires}; path=/`;
  document.cookie = `googtrans=${value}; expires=${expires}; path=/; domain=${host}`;
  document.cookie = `googtrans=${value}; expires=${expires}; path=/; domain=.${host}`;
};

let loading = false;
const loadWidget = () => {
  if (loading || window.google?.translate) return;
  loading = true;
  window.googleTranslateElementInit = () => {
    new window.google.translate.TranslateElement({ pageLanguage: SOURCE, includedLanguages: LANGUAGES.map((l) => l[0]).join(","), autoDisplay: false }, "google_translate_element");
  };
  const s = document.createElement("script");
  s.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  s.async = true;
  document.body.appendChild(s);
};

const applyLang = (lang) => {
  setCookie(lang);
  localStorage.setItem(STORAGE, lang);
  const combo = document.querySelector(".goog-te-combo");
  if (combo && lang !== SOURCE) {
    combo.value = lang;
    combo.dispatchEvent(new Event("change"));
    return;
  }
  window.location.reload();
};

/** Traductor multi-idioma de la página (Google Translate) con selector propio. */
export const LanguageSwitcher = ({ tone = "light" }) => {
  const [lang, setLang] = useState(readCookieLang);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    loadWidget();
    const close = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, []);

  const pick = (code) => {
    setOpen(false);
    if (code === lang) return;
    setLang(code);
    applyLang(code);
  };
  const current = LANGUAGES.find((l) => l[0] === lang) || LANGUAGES[0];
  const dark = tone === "light";

  return (
    <div ref={ref} className="relative notranslate" translate="no" data-testid="language-switcher">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={open} className={`inline-flex h-11 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-xs font-semibold uppercase tracking-[0.03em] transition-colors ${dark ? "border-white/20 text-white/85 hover:border-leaf hover:text-leaf" : "border-ink/15 text-ink hover:border-leaf-deep"}`} data-testid="language-button">
        <Globe className="h-4 w-4" /> <span className="hidden sm:inline">{current[2]} {current[0].split("-")[0]}</span><span className="sm:hidden">{current[2]}</span> <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul role="listbox" className="absolute right-0 z-[60] mt-2 grid w-64 grid-cols-2 gap-1 rounded-2xl border border-ink/10 bg-white p-2 text-ink shadow-2xl" data-testid="language-menu">
          {LANGUAGES.map(([code, name, flag]) => (
            <li key={code}>
              <button type="button" role="option" aria-selected={code === lang} onClick={() => pick(code)} className={`flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm hover:bg-paper ${code === lang ? "bg-leaf/15 font-semibold" : ""}`} data-testid={`language-${code}`}>
                <span>{flag}</span> <span className="flex-1 truncate">{name}</span> {code === lang && <Check className="h-3.5 w-3.5 text-leaf-deep" />}
              </button>
            </li>
          ))}
        </ul>
      )}
      <div id="google_translate_element" className="hidden" />
    </div>
  );
};
